import { deviceZone } from './schedule';

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

export class ApiError extends Error {
  // data: the whole answer, e.g. { code: 'no_seats', plan } for the screen to act on.
  constructor(message, status, data = {}) {
    super(message);
    this.status = status;
    this.code = data.code;
    this.data = data;
  }
}

// The website calls its own server with the session cookie and CSRF token. The native app ships its pages
// inside the app, so it calls the server by full URL with a Bearer token instead (see native/NativeApp).
const auth = { baseUrl: '', token: null, onUnauthorized: null, onMembership: null, onDenied: null };
export const configureApi = (options) => Object.assign(auth, options);

// Data loads in flight (GET only: saves and AI calls show their own spinners), for the top loading bar.
let pending = 0;
const loadingListeners = new Set();
const track = (delta) => {
  pending += delta;
  loadingListeners.forEach((listener) => listener(pending > 0));
};
export const subscribeLoading = (listener) => {
  loadingListeners.add(listener);
  return () => loadingListeners.delete(listener);
};

// With a share code the server reads that person's log (read-only); without one it uses your own.
// coachClient: a coach reading a connected student's log (read-only, what the student shares).
export const createApi = (basePath, shareCode = null, { coachClient = null } = {}) => {
  const request = async (method, path, body, options = {}) => {
    if (method !== 'GET') return send(method, path, body, options);
    track(1);
    try {
      return await send(method, path, body);
    } finally {
      track(-1);
    }
  };

  // `quiet`: a background call (ActivityPing) that never signs out, redirects or opens the Pro sheet.
  const send = async (method, path, body, { idempotencyKey, quiet = false } = {}) => {
    const isForm = body instanceof FormData;
    const response = await fetch(`${auth.baseUrl}${basePath}${path}`, {
      method,
      credentials: auth.token ? 'omit' : 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : { 'X-CSRF-Token': csrfToken() }),
        ...(shareCode ? { 'X-Share-Code': shareCode } : {}),
        ...(coachClient ? { 'X-Coach-Client': String(coachClient) } : {}),
        // "Today" is the phone's day: the server dates appointments in this zone.
        'X-Time-Zone': deviceZone(),
        // AI requests: the same key on a retry means the server charges the credit only once.
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        ...(body && !isForm ? { 'Content-Type': 'application/json' } : {}),
      },
      body: body ? (isForm ? body : JSON.stringify(body)) : undefined,
    });

    if (quiet) {
      if (!response.ok) throw new ApiError(`Có lỗi xảy ra (${response.status})`, response.status, {});
      return null;
    }
    if (response.status === 401 && auth.token) auth.onUnauthorized?.();
    // The website's session ended (signed out in another tab, expired): a change can't be saved, so sign in again.
    if (response.status === 401 && !auth.token && method !== 'GET') window.location.assign('/login');
    // AI actions send back today's credits; a refused one (no credits, Pro only) opens the Pro sheet.
    const membership = response.headers.get('X-Membership');
    if (membership) auth.onMembership?.(JSON.parse(membership));
    if (response.status === 204) return null;
    const data = await response.json().catch(() => ({}));
    if ((response.status === 402 || response.status === 403) && data.code) auth.onDenied?.(data);
    if (!response.ok) throw new ApiError(data.error || `Có lỗi xảy ra (${response.status})`, response.status, data);
    return data;
  };

  return {
    get: (path) => request('GET', path),
    post: (path, body, options) => request('POST', path, body, options),
    patch: (path, body) => request('PATCH', path, body),
    put: (path, body) => request('PUT', path, body),
    del: (path, body) => request('DELETE', path, body),
  };
};
